/* VicHoorn mobile sticky add-to-cart bar.
 * - Reveals after the user scrolls past N px (configurable per section).
 * - Mirrors variant selection from the main product form (#main-product variant inputs).
 * - Submits via fetch to /cart/add.js and opens the cart drawer (theme's pattern).
 */

class VHMobileSticky extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.variantInput = this.querySelector('[data-vh-variant-id]');
    this.priceWrap = this.querySelector('[data-vh-price]');
    this.showAfter = parseInt(this.dataset.showAfter || '400', 10);
    this.handle = this.dataset.productHandle;
    this.maxInstallments = parseInt(this.dataset.maxInstallments || '5', 10);
    this.moneyFormat = this.dataset.moneyFormat || (window.theme && theme.moneyFormat) || '${{amount}}';
    this.variants = this._readVariants();
    this._onScroll = this._onScroll.bind(this);
    this._onSubmit = this._onSubmit.bind(this);
    this._onMainFormChange = this._onMainFormChange.bind(this);
  }

  connectedCallback() {
    if (!this.classList.contains('is-hidden')) {
      this.classList.add('is-visible');
    }
    window.addEventListener('scroll', this._onScroll, { passive: true });
    this.form.addEventListener('submit', this._onSubmit);
    document.addEventListener('change', this._onMainFormChange, true);
    this._syncFromMain();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScroll);
    document.removeEventListener('change', this._onMainFormChange, true);
  }

  _onScroll() {
    const past = window.scrollY > this.showAfter;
    if (past) this.classList.remove('is-hidden');
    this.classList.toggle('is-visible', past);
  }

  /* If the user changes a variant in the main product form, mirror the variant
   * id and the price into the sticky bar. We listen broadly because Dawn does
   * not emit a single canonical event. */
  _onMainFormChange(e) {
    const form = e.target && e.target.closest && e.target.closest('form[action$="/cart/add"]');
    if (!form) return;
    if (form === this.form) return;
    // Defer so Dawn's product-info finishes its own DOM updates.
    setTimeout(() => this._syncFromMain(), 50);
  }

  _syncFromMain() {
    const mainForm = document.querySelector('product-info form[action$="/cart/add"]');
    if (!mainForm) return;
    const idInput = mainForm.querySelector('input[name="id"]');
    const variantId = idInput ? String(idInput.value) : '';
    if (idInput && this.variantInput) this.variantInput.value = variantId;

    const variant = this.variants.find((item) => String(item.id) === variantId);
    if (variant) {
      this._renderVariant(variant);
      return;
    }

    // Price: copy from rendered .price__regular / .price__sale wrappers if present.
    const price = document.querySelector('product-info .price');
    if (price && this.priceWrap) {
      const sale = price.querySelector('.price-item--sale, .price__sale .price-item--last');
      const regular = price.querySelector('.price-item--regular, .price__regular .price-item');
      const compare = price.querySelector('.price__sale s, .price-item--last s, s.price-item');
      const out = ['<span class="vh-mobile-sticky__price-row">'];
      if (compare) out.push(`<s class="vh-mobile-sticky__compare">${compare.textContent.trim()}</s>`);
      const current = sale || regular;
      if (current) {
        out.push(`<strong>${current.textContent.trim()}</strong>`);
        out.push('</span>');
        this.priceWrap.innerHTML = out.join('');
      }
    }
  }

  _readVariants() {
    const script = this.querySelector('[data-vh-sticky-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent || '[]');
    } catch (_) {
      return [];
    }
  }

  _formatMoney(cents) {
    const value = Number(cents || 0);
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(value, this.moneyFormat);
    }
    return 'R$ ' + (value / 100).toFixed(2).replace('.', ',');
  }

  _renderVariant(variant) {
    if (!this.priceWrap) return;

    const parts = ['<span class="vh-mobile-sticky__price-row">'];
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      parts.push(`<s class="vh-mobile-sticky__compare">${this._formatMoney(variant.compare_at_price)}</s>`);
    }
    parts.push(`<strong>${this._formatMoney(variant.price)}</strong>`);
    parts.push('</span>');

    if (this.maxInstallments > 1) {
      const installment = Math.floor(Number(variant.price || 0) / this.maxInstallments);
      parts.push(`<span class="vh-mobile-sticky__installments" data-vh-installments>${this.maxInstallments}x de ${this._formatMoney(installment)} sem juros</span>`);
    }

    this.priceWrap.innerHTML = parts.join('');

    const button = this.form && this.form.querySelector('button[type="submit"]');
    if (button) button.disabled = !variant.available;
  }

  async _onSubmit(e) {
    e.preventDefault();
    const cart = this._resolveCart();
    const fd = new FormData(this.form);
    const submitBtn = this.form.querySelector('button[type="submit"]');
    if (submitBtn && (submitBtn.disabled || submitBtn.getAttribute('aria-disabled') === 'true')) return;

    if (cart) {
      fd.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      fd.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }

    this._setLoading(submitBtn, true);
    try {
      const config = typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : { method: 'POST', headers: { Accept: 'application/javascript' } };

      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = fd;

      const res = await fetch(window.routes?.cart_add_url || this.form.action, config);
      let data;
      try {
        data = await res.json();
      } catch (e) {
        if (!res.ok) throw new Error(`Cart add failed: HTTP ${res.status} (non-JSON response)`);
        throw e;
      }
      if (!res.ok || data.status) throw data;

      if (!cart) {
        window.location.href = window.routes?.cart_url || '/cart';
        return;
      }

      if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'vh-mobile-sticky',
          productVariantId: fd.get('id'),
          cartData: data,
        });
      }

      this._notifyCart(data, cart);
      this._setLoading(submitBtn, false);
    } catch (err) {
      if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'vh-mobile-sticky',
          productVariantId: fd.get('id'),
          errors: err.errors || err.description,
          message: err.message,
        });
      }
      console.error('[vh-mobile-sticky] cart add failed', err);
      this._setLoading(submitBtn, false);
    } finally {
      this._syncFromMain();
    }
  }

  _resolveCart() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function' && typeof drawer.getSectionsToRender === 'function') {
      return drawer;
    }

    const notification = document.querySelector('cart-notification');
    if (
      notification &&
      typeof notification.renderContents === 'function' &&
      typeof notification.getSectionsToRender === 'function'
    ) {
      return notification;
    }

    return null;
  }

  _setLoading(submitBtn, isLoading) {
    if (!submitBtn) return;

    const spinner = submitBtn.querySelector('.loading__spinner');
    if (isLoading) {
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('loading');
      spinner?.classList.remove('hidden');
      return;
    }

    submitBtn.removeAttribute('aria-disabled');
    submitBtn.classList.remove('loading');
    spinner?.classList.add('hidden');
  }

  _notifyCart(data, cart) {
    cart.renderContents(data);
  }
}

if (!customElements.get('vh-mobile-sticky')) {
  customElements.define('vh-mobile-sticky', VHMobileSticky);
}
